import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import Logo from "@/components/brand/Logo";

const NAV_LINKS = [
    { to: "/", label: "Home" },
    { to: "/about", label: "About" },
    { to: "/future-builders-lab", label: "Future Builders Lab" },
    { to: "/sponsor", label: "Sponsor a Student" },
    { to: "/student-interest", label: "Student Interest" },
    { to: "/parent-info", label: "Parent Info" },
    { to: "/partner", label: "Partner With Us" },
    { to: "/contact", label: "Contact" },
];

export const Header = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const drawerRef = useRef(null);
    const lastFocusRef = useRef(null);
    const location = useLocation();
    const reduce = useReducedMotion();

    /* Scroll listener for header style change */
    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    /* Close drawer on route change */
    useEffect(() => {
        if (open) setOpen(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    /* ESC to close + focus trap + body lock */
    useEffect(() => {
        if (!open) return;
        lastFocusRef.current = document.activeElement;
        document.body.style.overflow = "hidden";
        const onKey = (e) => {
            if (e.key === "Escape") setOpen(false);
            if (e.key === "Tab" && drawerRef.current) {
                const focusables = drawerRef.current.querySelectorAll(
                    'a, button, [tabindex]:not([tabindex="-1"])',
                );
                if (!focusables.length) return;
                const first = focusables[0];
                const last = focusables[focusables.length - 1];
                if (e.shiftKey && document.activeElement === first) {
                    last.focus();
                    e.preventDefault();
                } else if (!e.shiftKey && document.activeElement === last) {
                    first.focus();
                    e.preventDefault();
                }
            }
        };
        document.addEventListener("keydown", onKey);

        /* Focus first nav link */
        const t = setTimeout(() => {
            const first = drawerRef.current?.querySelector("a, button");
            first?.focus();
        }, 60);

        return () => {
            document.removeEventListener("keydown", onKey);
            clearTimeout(t);
            document.body.style.overflow = "";
            lastFocusRef.current?.focus?.();
        };
    }, [open]);

    return (
        <>
            <a href="#main-content" className="vfs-skip">
                Skip to main content
            </a>

            <header
                data-testid="site-header"
                className="fixed top-0 left-0 right-0 z-40 transition-all duration-300"
                style={{
                    background: scrolled
                        ? "rgba(15, 7, 32, 0.85)"
                        : "rgba(15, 7, 32, 0.35)",
                    backdropFilter: "blur(16px) saturate(160%)",
                    WebkitBackdropFilter: "blur(16px) saturate(160%)",
                    borderBottom: scrolled
                        ? "1px solid rgba(255,255,255,0.08)"
                        : "1px solid transparent",
                }}
            >
                <div className="vfs-container flex items-center justify-between py-3.5 lg:py-4">
                    <Link
                        to="/"
                        aria-label="Vibe for Success — Home"
                        data-testid="header-logo-link"
                    >
                        <Logo size={32} />
                    </Link>

                    {/* Desktop nav */}
                    <nav
                        aria-label="Primary"
                        className="hidden lg:flex items-center gap-7"
                    >
                        {NAV_LINKS.slice(0, 3).map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                data-testid={`nav-${l.to.replace(/\//g, "") || "home"}`}
                                className={({ isActive }) =>
                                    `text-sm font-medium transition-colors hover:text-white ${
                                        isActive
                                            ? "text-white"
                                            : "text-[var(--text-secondary)]"
                                    }`
                                }
                            >
                                {l.label}
                            </NavLink>
                        ))}
                    </nav>

                    {/* Right CTAs (desktop) */}
                    <div className="hidden lg:flex items-center gap-3">
                        <Link
                            to="/student-interest"
                            className="vfs-cta-outline text-sm"
                            data-testid="header-secondary-cta"
                            style={{ padding: "0.6rem 1.1rem" }}
                        >
                            Student / Parent Interest
                        </Link>
                        <Link
                            to="/sponsor"
                            className="vfs-cta text-sm"
                            data-testid="header-primary-cta"
                            style={{ padding: "0.6rem 1.3rem" }}
                        >
                            Sponsor a Student
                        </Link>
                    </div>

                    {/* Mobile menu button */}
                    <button
                        type="button"
                        className="lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-xl border border-white/10 bg-white/5"
                        aria-label="Open menu"
                        aria-expanded={open}
                        aria-controls="mobile-drawer"
                        data-testid="mobile-menu-toggle"
                        onClick={() => setOpen(true)}
                    >
                        <Menu size={22} color="#F5F3FF" />
                    </button>
                </div>
            </header>

            {/* Mobile drawer */}
            <AnimatePresence>
                {open && (
                    <motion.div
                        id="mobile-drawer"
                        role="dialog"
                        aria-modal="true"
                        aria-label="Site navigation"
                        ref={drawerRef}
                        data-testid="mobile-drawer"
                        className="fixed inset-0 z-50 lg:hidden"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{
                            duration: reduce ? 0 : 0.25,
                            ease: "easeOut",
                        }}
                        style={{
                            background: "rgba(15, 7, 32, 0.97)",
                            backdropFilter: "blur(24px) saturate(160%)",
                            WebkitBackdropFilter: "blur(24px) saturate(160%)",
                        }}
                    >
                        <div className="vfs-container py-5 flex items-center justify-between">
                            <Logo size={32} />
                            <button
                                type="button"
                                className="inline-flex items-center justify-center w-11 h-11 rounded-xl border border-white/10 bg-white/5"
                                aria-label="Close menu"
                                data-testid="mobile-menu-close"
                                onClick={() => setOpen(false)}
                            >
                                <X size={22} color="#F5F3FF" />
                            </button>
                        </div>
                        <nav
                            aria-label="Mobile primary"
                            className="vfs-container flex flex-col gap-2 pt-4"
                        >
                            {NAV_LINKS.map((l) => (
                                <Link
                                    key={l.to}
                                    to={l.to}
                                    data-testid={`mobile-nav-${l.to.replace(/\//g, "") || "home"}`}
                                    className="block py-3 px-3 rounded-xl text-lg font-medium text-[var(--text-primary)] hover:bg-white/5 transition-colors"
                                >
                                    {l.label}
                                </Link>
                            ))}
                            <div className="mt-6 flex flex-col gap-3">
                                <Link
                                    to="/sponsor"
                                    className="vfs-cta"
                                    data-testid="mobile-primary-cta"
                                >
                                    Sponsor a Student
                                </Link>
                                <Link
                                    to="/student-interest"
                                    className="vfs-cta-outline"
                                    data-testid="mobile-secondary-cta"
                                >
                                    Student / Parent Interest
                                </Link>
                            </div>
                        </nav>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default Header;
